import { isEmpty, toNumber } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  Banner,
  Loader,
  PaymentMethodsForm,
  SubventionForm,
  TravelPurchaseSummary,
  useTravelPurchaseContext
} from 'components';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { setTravelPurchase } from 'store';

const TravelValidationStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { watch } = useFormContext();
  const { next } = useTravelPurchaseContext();
  const { currentUser } = useUser();
  const { call: newsletterCall } = useFetch({ withSetState: false });
  const { call: cartCall, data: cart } = useFetch();
  const { call: createPaymentCall, loading: createPaymentLoading } = useFetch({ withSetState: false });
  const { call: subventionSaveCall } = useFetch({ withSetState: false });

  const [searchParams] = useSearchParams();
  const newsletterChoice = watch('validation.newsletter');
  const subventionChoice = watch('validation.subventionChoice');
  const subventionAmount = watch('validation.subventionAmount');
  const depositChoice = watch('validation.depositChoice');
  const paymentMethod = watch('validation.paymentMethod');
  const salesTerms = watch('validation.salesTerms');
  const specificSalesTerms = watch('validation.specificSalesTerms');
  const paymentMethodChoice = watch('validation.paymentMethod');
  const travelId = searchParams.get('travelId');
  const typeItem = searchParams.get('typeItem');
  const updatedCart = useSelector((state) => state.cart.cart);

  const isStepValid = useMemo(() => {
    return (
      (subventionChoice === 'withoutSubvention' ||
        (subventionChoice === 'withSubvention' && watch('validation.subventionValid'))) &&
      !!depositChoice &&
      !!paymentMethod &&
      salesTerms &&
      specificSalesTerms
    );
  }, [
    subventionChoice,
    subventionAmount,
    depositChoice,
    paymentMethod,
    salesTerms,
    specificSalesTerms,
    watch('validation.subventionValid')
  ]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getCart();
  }, []);

  useEffect(() => {
    const depositAmount =
      (isEmpty(updatedCart)
        ? cart?.availablePaymentMode?.withDeposit?.amount
        : updatedCart?.availablePaymentMode?.withDeposit?.amount || 0) / 100;
    dispatch(
      setTravelPurchase({ key: 'depositAmount', value: depositChoice === 'withDeposit' ? depositAmount : null })
    );
  }, [depositChoice, cart, updatedCart]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getCart = () => {
    if (!currentUser?.serialNumber) return;

    cartCall({
      service: 'checkout',
      method: 'get',
      path: `/${currentUser.serialNumber}/cart/travel/`
    });
  };

  const getCallBackUrl = (type, travelId, orderId) => {
    if (appService.isArrivaj()) {
      const baseUrl = window.location.origin;
      const path =
        type === 'confirmation'
          ? routes.travelPurchase.children.confirmation.path
          : routes.travelPurchase.children.validation.path;

      const additionalParams = type === 'confirmation' ? `&orderId=${orderId}` : '&error=paymentFailed';

      return `${baseUrl}${path}?typeItem=${typeItem}&travelId=${travelId}${additionalParams}`;
    }
  };

  const getPaymentChoices = async () => {
    if (isEmpty(currentUser)) return;
    const doneCallback = getCallBackUrl('confirmation', travelId, cart.id);
    const errorCallback = getCallBackUrl('validation', travelId);
    let endpoint = `/${currentUser.serialNumber}/payment/travel/create/${paymentMethodChoice}`;
    if (depositChoice === 'withDeposit') endpoint += '/deposit';

    const { data } =
      (await createPaymentCall({
        service: 'checkout',
        method: 'post',
        path: endpoint,
        data: {
          doneCallback,
          errorCallback
        }
      })) || {};

    if (!isEmpty(data?.body)) {
      if (data.body?.type === 'INTEGRATED_FORM') {
        dispatch(setTravelPurchase({ key: 'paymentHead', value: data.body.content.head }));
        dispatch(setTravelPurchase({ key: 'paymentBody', value: data.body.content.body }));
        next();
      } else if (data.body?.type === 'REDIRECTION') {
        window.location = data.body.content.url;
      }
    }
  };

  const saveNewsletterChoice = async () => {
    if (currentUser) {
      const { data } =
        (await newsletterCall({
          service: 'castelis',
          method: 'post',
          path: `/users/newsletter/${currentUser.serialNumber}`,
          query: {
            select: newsletterChoice
          }
        })) || {};
      if (data?.status === 200) {
        dispatch(setTravelPurchase({ key: 'newsletter', value: newsletterChoice }));
      }
    }
  };

  const onNextClick = async () => {
    if (!isStepValid) return;
    // Save payment method and deposit in the store
    dispatch(setTravelPurchase({ key: 'paymentMethod', value: paymentMethodChoice }));
    dispatch(setTravelPurchase({ key: 'depositChoice', value: depositChoice }));

    if (subventionChoice === 'withoutSubvention' && subventionAmount > 0) {
      dispatch(setTravelPurchase({ key: 'subvention', value: 0 }));

      await subventionSaveCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/cart/travel/subsidize`,
        data: {
          employee: {
            ...cart.employee,
            companyId: toNumber(currentUser.businessId)
          },
          lines: cart.lines.map((line) => ({
            subsidizedId: line.subsidizedInfo.subsidizedId,
            selectedSubsidized: 0
          }))
        }
      });
    }

    await saveNewsletterChoice();
    await getPaymentChoices();
  };

  return (
    <>
      <Banner type="warning" icon={{ name: 'circle-info', style: 'fad' }} className="!hidden lg:!flex lg:mb-8">
        <p>{t('purchase:validation.reminder')}</p>
      </Banner>

      <TravelPurchaseSummary isValidationStep className="lg:hidden" skeletonClassName="lg:hidden" />

      <SubventionForm cart={cart} />
      <PaymentMethodsForm cart={cart} />

      <button
        type="button"
        className="btn my-4 max-lg:w-full lg:ml-auto"
        disabled={!isStepValid || createPaymentLoading}
        onClick={onNextClick}
      >
        {createPaymentLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
        {t('buttons.pay')}
      </button>
    </>
  );
};

export default TravelValidationStep;
